import React, { useState, useEffect, useMemo } from 'react';
import { studentService, classService } from '../../services/index.js';
import * as XLSX from 'xlsx';
import jsPDF from 'jspdf';
import { 
  Users, UserCheck, UserX, AlertCircle, FileText, Search, Filter, Download, Printer, Calendar, RefreshCw, ChevronLeft, ChevronRight, Eye, X, BookOpen, Layers
} from 'lucide-react';

const StudentMasterList = ({ orgData, students: propStudents = [], classes: propClasses = [], showNotification }) => {
  const orgId = orgData?.orgId || orgData?.id || 'SCH-02';

  // Core Data States
  const [students, setStudents] = useState(propStudents || []);
  const [classes, setClasses] = useState(propClasses || []);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState(null);

  // View Profile Modal State
  const [selectedStudentForView, setSelectedStudentForView] = useState(null);

  // Filter States
  const [academicYear, setAcademicYear] = useState('ALL');
  const [filterClass, setFilterClass] = useState('');
  const [filterSection, setFilterSection] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [searchTerm, setSearchTerm] = useState('');

  // Pagination States
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);

  // Sync props
  useEffect(() => {
    if (Array.isArray(propStudents) && propStudents.length > 0) setStudents(propStudents);
    if (Array.isArray(propClasses) && propClasses.length > 0) setClasses(propClasses);
  }, [propStudents, propClasses]);

  // Initial Load
  useEffect(() => {
    loadStudentMasterDirectory();
  }, [orgId]);

  const loadStudentMasterDirectory = async () => {
    setLoading(true);
    setLoadError(null);
    try {
      if (!propStudents || propStudents.length === 0) {
        const res = await studentService.getStudents();
        const list = Array.isArray(res) ? res : (res?.data || res?.students || []);
        setStudents(list);
      }
      if (!propClasses || propClasses.length === 0) {
        const resCls = await classService.getAllClasses();
        const listCls = Array.isArray(resCls) ? resCls : (resCls?.data || resCls?.classes || []);
        setClasses(listCls);
      }
    } catch (err) {
      console.error('Error loading student master list:', err);
      setLoadError('Unable to load student directory records from backend server');
    } finally {
      setLoading(false);
    }
  };

  // Helper date formatting
  const formatDate = (dateStr) => {
    if (!dateStr || dateStr === 'Invalid Date') return '—';
    try {
      const d = new Date(dateStr);
      if (isNaN(d.getTime())) return dateStr;
      return d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
    } catch (e) {
      return dateStr;
    }
  };

  // Extract unique classes and sections
  const availableClasses = useMemo(() => {
    const setCls = new Set();
    students.forEach(s => {
      const c = s.className || s.class;
      if (c) setCls.add(String(c));
    });
    classes.forEach(c => {
      if (c.className || c.name) setCls.add(String(c.className || c.name));
    });
    return Array.from(setCls).sort((a, b) => a.localeCompare(b, undefined, { numeric: true }));
  }, [students, classes]);

  const availableSections = useMemo(() => {
    const setSec = new Set();
    students.forEach(s => {
      if (s.section) setSec.add(String(s.section));
    });
    return Array.from(setSec).sort();
  }, [students]);

  // Filtered Students List
  const filteredStudents = useMemo(() => {
    return students.filter(s => {
      // Academic Year Filter
      const sYear = s.admissionYear;
      const matchYear = !academicYear || academicYear === 'ALL' || !sYear || String(sYear) === String(academicYear);

      // Class & Section Filter
      const sClass = s.className || s.class || '';
      const sSec = s.section || '';
      const matchClass = !filterClass || String(sClass).toLowerCase() === String(filterClass).toLowerCase();
      const matchSection = !filterSection || String(sSec).toLowerCase() === String(filterSection).toLowerCase();

      // Status Filter
      const statusStr = (s.registrationStatus || s.status || 'Active').toUpperCase();
      let matchStatus = true;
      if (filterStatus === 'ACTIVE') {
        matchStatus = statusStr === 'APPROVED' || statusStr === 'ACTIVE';
      } else if (filterStatus === 'INACTIVE') {
        matchStatus = statusStr === 'INACTIVE' || statusStr === 'WITHDRAWN' || statusStr === 'CANCELLED' || statusStr === 'REJECTED';
      } else if (filterStatus === 'UNASSIGNED') {
        matchStatus = !sClass || sClass.trim() === '';
      }

      // Search Filter
      const q = searchTerm.toLowerCase().trim();
      const matchSearch = !q || 
        (s.name || s.studentName || '').toLowerCase().includes(q) ||
        (s.studentId || '').toLowerCase().includes(q) ||
        (s.admissionNo || '').toLowerCase().includes(q) ||
        (s.rollNo || '').toLowerCase().includes(q);

      return matchYear && matchClass && matchSection && matchStatus && matchSearch;
    });
  }, [students, academicYear, filterClass, filterSection, filterStatus, searchTerm]);

  // KPI Calculations
  const kpis = useMemo(() => {
    const total = filteredStudents.length;

    const activeCount = filteredStudents.filter(s => {
      const st = (s.registrationStatus || s.status || '').toUpperCase();
      return st === 'APPROVED' || st === 'ACTIVE' || st === '';
    }).length;

    const inactiveCount = filteredStudents.filter(s => {
      const st = (s.registrationStatus || s.status || '').toUpperCase();
      return st === 'INACTIVE' || st === 'WITHDRAWN' || st === 'CANCELLED' || st === 'REJECTED';
    }).length;

    const unassignedCount = filteredStudents.filter(s => {
      const c = s.className || s.class;
      return !c || String(c).trim() === '';
    }).length;

    return { total, activeCount, inactiveCount, unassignedCount };
  }, [filteredStudents]);

  // Reset pagination when filter changes
  useEffect(() => {
    setCurrentPage(1);
  }, [academicYear, filterClass, filterSection, filterStatus, searchTerm, pageSize]);

  // Paginated Data Slice
  const totalPages = Math.ceil(filteredStudents.length / pageSize) || 1;
  const paginatedStudents = useMemo(() => {
    const start = (currentPage - 1) * pageSize;
    return filteredStudents.slice(start, start + pageSize);
  }, [filteredStudents, currentPage, pageSize]);

  // Export Excel / CSV
  const handleExportExcel = () => {
    if (filteredStudents.length === 0) {
      if (showNotification) showNotification('warning', 'No Data', 'No student records available to export');
      return;
    }

    const exportData = filteredStudents.map(s => ({
      'Student ID': s.studentId || s.id || '—',
      'Roll Number': s.rollNo || '—',
      'Student Name': s.name || s.studentName || '—',
      'Class': s.className || s.class || '—',
      'Section': s.section || '—',
      'Gender': s.gender || '—',
      'Father / Guardian Name': s.fatherName || s.parentName || '—',
      'Contact Phone': s.phone || s.parentPhone || '—',
      'Status': s.registrationStatus || s.status || 'Active',
      'City / Address': s.city || s.address || '—'
    }));

    const worksheet = XLSX.utils.json_to_sheet(exportData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Student_Master_List');
    XLSX.writeFile(workbook, `Student_Master_List_${new Date().toISOString().split('T')[0]}.xlsx`);
    if (showNotification) showNotification('success', 'Export Complete', 'Student Master List Excel downloaded successfully');
  };

  // Export PDF
  const handleExportPDF = () => {
    if (filteredStudents.length === 0) {
      if (showNotification) showNotification('warning', 'No Data', 'No student records available to export');
      return;
    }

    try {
      const doc = new jsPDF();
      const schoolName = orgData?.orgName || orgData?.institutionName || orgData?.name || 'Institution';
      
      // Header
      doc.setFontSize(16);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(37, 99, 235);
      doc.text(schoolName, 14, 15);

      doc.setFontSize(12);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(15, 23, 42);
      doc.text('Student Master Directory Report', 14, 23);

      doc.setFontSize(9);
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(100, 116, 139);
      doc.text(`Generated Date: ${new Date().toLocaleDateString('en-IN')} | Total Enrolled: ${filteredStudents.length}`, 14, 29);

      // Line Separator
      doc.setDrawColor(226, 232, 240);
      doc.line(14, 32, 196, 32);

      // Table Headers
      let y = 40;
      doc.setFontSize(9);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(15, 23, 42);
      doc.text("Student ID", 14, y);
      doc.text("Student Name", 50, y);
      doc.text("Class", 110, y);
      doc.text("Sec", 130, y);
      doc.text("Roll No", 145, y);
      doc.text("Phone", 165, y);

      y += 4;
      doc.setDrawColor(203, 213, 225);
      doc.line(14, y, 196, y);
      y += 6;

      // Table Rows
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(51, 65, 85);

      filteredStudents.forEach((s) => {
        if (y > 280) {
          doc.addPage();
          y = 20;
        }

        const stId = s.studentId || s.id || '—';
        const name = (s.name || s.studentName || 'Student').substring(0, 25);
        const cls = `Class ${s.className || s.class || '—'}`;
        const sec = s.section || 'A';
        const roll = s.rollNo || '—';
        const phone = s.phone || s.parentPhone || '—';

        doc.text(String(stId), 14, y);
        doc.text(String(name), 50, y);
        doc.text(String(cls), 110, y);
        doc.text(String(sec), 130, y);
        doc.text(String(roll), 145, y);
        doc.text(String(phone), 165, y);

        y += 7;
      });

      doc.save(`Student_Master_List_${schoolName.replace(/\s+/g, '_')}_${new Date().toISOString().split('T')[0]}.pdf`);
      if (showNotification) showNotification('success', 'Export Complete', 'Student Master List PDF downloaded successfully');
    } catch (err) {
      console.error('PDF export error:', err);
      if (showNotification) showNotification('error', 'Export Failed', 'Failed to generate PDF document');
    }
  };

  return (
    <div style={{ padding: '24px', background: '#f8fafc', minHeight: '100vh' }}>
      {/* Top Header Card */}
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        background: '#ffffff',
        padding: '20px 24px',
        borderRadius: '12px',
        border: '1px solid #e2e8f0',
        marginBottom: '20px',
        boxShadow: '0 1px 3px rgba(0, 0, 0, 0.05)',
        flexWrap: 'wrap',
        gap: '16px'
      }}>
        <div>
          <h1 style={{ fontSize: '20px', fontWeight: '800', color: '#0f172a', margin: 0, display: 'flex', alignItems: 'center', gap: '10px' }}>
            <Users size={24} color="#2563eb" /> Student Master List Directory
          </h1>
          <p style={{ color: '#64748b', fontSize: '13px', margin: '4px 0 0 34px' }}>
            Complete directory of currently enrolled students across all classes and sections for {orgData?.orgName || orgData?.institutionName || orgData?.name || 'your institution'}
          </p>
        </div>

        <div style={{ display: 'flex', gap: '10px' }}>
          <button 
            onClick={handleExportExcel}
            disabled={loading || filteredStudents.length === 0}
            style={{ padding: '9px 16px', background: filteredStudents.length === 0 ? '#cbd5e1' : '#059669', color: '#fff', border: 'none', borderRadius: '8px', fontSize: '13px', fontWeight: '600', cursor: filteredStudents.length === 0 ? 'not-allowed' : 'pointer', display: 'flex', alignItems: 'center', gap: '6px' }}
          >
            <Download size={15} /> Export Excel
          </button>

          <button 
            onClick={handleExportPDF}
            disabled={loading || filteredStudents.length === 0}
            style={{ padding: '9px 16px', background: filteredStudents.length === 0 ? '#cbd5e1' : '#2563eb', color: '#fff', border: 'none', borderRadius: '8px', fontSize: '13px', fontWeight: '600', cursor: filteredStudents.length === 0 ? 'not-allowed' : 'pointer', display: 'flex', alignItems: 'center', gap: '6px' }}
          >
            <Printer size={15} /> Export PDF
          </button>
        </div>
      </div>

      {/* ── State 4: ERROR UI State ── */}
      {loadError && (
        <div style={{ marginBottom: '20px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', background: '#fef2f2', border: '1px solid #fca5a5', color: '#991b1b', padding: '14px 20px', borderRadius: '10px', fontSize: '13px', fontWeight: '500' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <AlertCircle size={20} color="#dc2626" />
            <span><strong>Server Error:</strong> {loadError}</span>
          </div>
          <button onClick={loadStudentMasterDirectory} style={{ background: '#dc2626', color: '#fff', border: 'none', padding: '7px 14px', borderRadius: '6px', fontSize: '12.5px', cursor: 'pointer', fontWeight: '600', display: 'flex', alignItems: 'center', gap: '6px' }}>
            <RefreshCw size={14} /> Retry Loading
          </button>
        </div>
      )}

      {/* ── Global Directory Filter Bar ── */}
      <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', alignItems: 'center', background: '#ffffff', padding: '14px 20px', borderRadius: '12px', marginBottom: '20px', border: '1px solid #e2e8f0', boxShadow: '0 1px 2px rgba(0,0,0,0.03)' }}>
        
        {/* Search Input */}
        <div style={{ flex: '1 1 220px', minWidth: '200px' }}>
          <div style={{ position: 'relative' }}>
            <Search size={15} color="#94a3b8" style={{ position: 'absolute', left: '10px', top: '50%', transform: 'translateY(-50%)' }} />
            <input 
              type="text" 
              placeholder="Search student, ID, or roll no..."
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
              style={{ width: '100%', padding: '8px 12px 8px 32px', border: '1px solid #cbd5e1', borderRadius: '6px', fontSize: '12.5px', outline: 'none', boxSizing: 'border-box' }}
            />
          </div>
        </div>

        {/* Academic Year */}
        <div style={{ width: '135px' }}>
          <select 
            value={academicYear} 
            onChange={e => setAcademicYear(e.target.value)}
            style={{ width: '100%', padding: '8px 10px', border: '1px solid #cbd5e1', borderRadius: '6px', fontSize: '12.5px', background: '#fff', outline: 'none' }}
          >
            <option value="ALL">All AY Years</option>
            <option value="2026-27">AY 2026-27</option>
            <option value="2025-26">AY 2025-26</option>
          </select>
        </div>

        {/* Class Filter */}
        <div style={{ width: '135px' }}>
          <select 
            value={filterClass} 
            onChange={e => setFilterClass(e.target.value)}
            style={{ width: '100%', padding: '8px 10px', border: '1px solid #cbd5e1', borderRadius: '6px', fontSize: '12.5px', background: '#fff', outline: 'none' }}
          >
            <option value="">All Classes</option>
            {availableClasses.map(cls => (
              <option key={cls} value={cls}>Class {cls}</option>
            ))}
          </select>
        </div>

        {/* Section Filter */}
        <div style={{ width: '120px' }}>
          <select 
            value={filterSection} 
            onChange={e => setFilterSection(e.target.value)}
            style={{ width: '100%', padding: '8px 10px', border: '1px solid #cbd5e1', borderRadius: '6px', fontSize: '12.5px', background: '#fff', outline: 'none' }}
          >
            <option value="">All Sections</option>
            {availableSections.map(sec => (
              <option key={sec} value={sec}>Sec {sec}</option>
            ))}
          </select>
        </div>

        {/* Status Filter */}
        <div style={{ width: '140px' }}>
          <select 
            value={filterStatus} 
            onChange={e => setFilterStatus(e.target.value)}
            style={{ width: '100%', padding: '8px 10px', border: '1px solid #cbd5e1', borderRadius: '6px', fontSize: '12.5px', background: '#fff', outline: 'none' }}
          >
            <option value="">All Statuses</option>
            <option value="ACTIVE">Active Enrolled</option>
            <option value="INACTIVE">Inactive / Withdrawn</option>
            <option value="UNASSIGNED">Unassigned Class</option>
          </select>
        </div>

        {(filterClass || filterSection || filterStatus || searchTerm || academicYear !== 'ALL') && (
          <button 
            onClick={() => { setFilterClass(''); setFilterSection(''); setFilterStatus(''); setSearchTerm(''); setAcademicYear('ALL'); }}
            style={{ background: 'none', border: 'none', color: '#ef4444', fontSize: '12px', fontWeight: '600', cursor: 'pointer', marginLeft: 'auto' }}
          >
            Reset Filters
          </button>
        )}
      </div>

      {/* ── 4 KPI Summary Cards ── */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '16px', marginBottom: '24px' }}>
        
        <div style={{ background: '#ffffff', padding: '20px', borderRadius: '12px', border: '1px solid #e2e8f0', boxShadow: '0 1px 3px rgba(0,0,0,0.04)' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '14px' }}>
            <div style={{ width: '46px', height: '46px', borderRadius: '10px', background: '#eff6ff', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <Users size={24} color="#2563eb" />
            </div>
            <div>
              <span style={{ fontSize: '12px', fontWeight: '600', color: '#64748b' }}>Total Students</span>
              <h3 style={{ fontSize: '24px', fontWeight: '800', color: '#0f172a', margin: '2px 0 0 0' }}>{kpis.total}</h3>
            </div>
          </div>
        </div>

        <div style={{ background: '#ffffff', padding: '20px', borderRadius: '12px', border: '1px solid #e2e8f0', boxShadow: '0 1px 3px rgba(0,0,0,0.04)' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '14px' }}>
            <div style={{ width: '46px', height: '46px', borderRadius: '10px', background: '#f0fdf4', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <UserCheck size={24} color="#16a34a" />
            </div>
            <div>
              <span style={{ fontSize: '12px', fontWeight: '600', color: '#64748b' }}>Active Enrolled</span>
              <h3 style={{ fontSize: '24px', fontWeight: '800', color: '#0f172a', margin: '2px 0 0 0' }}>{kpis.activeCount}</h3>
            </div>
          </div>
        </div>

        <div style={{ background: '#ffffff', padding: '20px', borderRadius: '12px', border: '1px solid #e2e8f0', boxShadow: '0 1px 3px rgba(0,0,0,0.04)' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '14px' }}>
            <div style={{ width: '46px', height: '46px', borderRadius: '10px', background: '#fef2f2', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <UserX size={24} color="#dc2626" />
            </div>
            <div>
              <span style={{ fontSize: '12px', fontWeight: '600', color: '#64748b' }}>Inactive / Withdrawn</span>
              <h3 style={{ fontSize: '24px', fontWeight: '800', color: '#0f172a', margin: '2px 0 0 0' }}>{kpis.inactiveCount}</h3>
            </div>
          </div>
        </div>

        <div style={{ background: '#ffffff', padding: '20px', borderRadius: '12px', border: '1px solid #e2e8f0', boxShadow: '0 1px 3px rgba(0,0,0,0.04)' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '14px' }}>
            <div style={{ width: '46px', height: '46px', borderRadius: '10px', background: '#fffbeb', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <Layers size={24} color="#d97706" />
            </div>
            <div>
              <span style={{ fontSize: '12px', fontWeight: '600', color: '#64748b' }}>Unassigned Class</span>
              <h3 style={{ fontSize: '24px', fontWeight: '800', color: '#0f172a', margin: '2px 0 0 0' }}>{kpis.unassignedCount}</h3>
            </div>
          </div>
        </div>

      </div>

      {/* ── Main Directory Table Workspace ── */}
      <div style={{ background: '#ffffff', borderRadius: '12px', border: '1px solid #e2e8f0', padding: '20px' }}>
        
        {/* Table Top Controls */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px', flexWrap: 'wrap', gap: '12px' }}>
          <h3 style={{ fontSize: '16px', fontWeight: '700', color: '#0f172a', margin: 0 }}>Enrolled Student Roster</h3>
          
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <span style={{ fontSize: '12px', color: '#64748b' }}>Per Page:</span>
            <select 
              value={pageSize} 
              onChange={e => setPageSize(Number(e.target.value))}
              style={{ padding: '4px 8px', border: '1px solid #cbd5e1', borderRadius: '6px', fontSize: '12px' }}
            >
              <option value={10}>10</option>
              <option value={25}>25</option>
              <option value={50}>50</option>
              <option value={100}>100</option>
            </select>
          </div>
        </div>

        {/* ── State 1: LOADING UI State ── */}
        {loading ? (
          <div style={{ textAlign: 'center', padding: '50px', color: '#64748b' }}>
            <RefreshCw size={28} className="spin" style={{ marginBottom: '12px', color: '#2563eb' }} />
            <h4 style={{ margin: '0 0 4px 0', color: '#1e293b' }}>Loading Student Directory...</h4>
            <p style={{ margin: 0, fontSize: '13px' }}>Fetching complete student directory from database.</p>
          </div>
        ) : filteredStudents.length === 0 ? (
          /* ── State 3: SUCCESS EMPTY UI State ── */
          <div style={{ padding: '50px', textAlign: 'center', color: '#64748b' }}>
            <Users size={40} color="#94a3b8" style={{ marginBottom: '12px' }} />
            <h4 style={{ margin: '0 0 6px 0', fontSize: '15px', color: '#334155' }}>No students found for the selected filters</h4>
            <p style={{ margin: 0, fontSize: '13px' }}>Try adjusting your search criteria, class filter, or status selection.</p>
          </div>
        ) : (
          /* ── State 2: SUCCESS WITH DATA ── */
          <>
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
                <thead>
                  <tr style={{ background: '#f8fafc', borderBottom: '1px solid #e2e8f0', textAlign: 'left' }}>
                    <th style={{ padding: '12px' }}>Student ID</th>
                    <th style={{ padding: '12px' }}>Student Name</th>
                    <th style={{ padding: '12px' }}>Class</th>
                    <th style={{ padding: '12px' }}>Section</th>
                    <th style={{ padding: '12px' }}>Roll No</th>
                    <th style={{ padding: '12px' }}>Parent / Guardian</th>
                    <th style={{ padding: '12px' }}>Contact</th>
                    <th style={{ padding: '12px' }}>Status</th>
                    <th style={{ padding: '12px', textAlign: 'right' }}>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {paginatedStudents.map((s, idx) => {
                    const stId = s.studentId || s.id || `STU-${idx}`;
                    const statusStr = (s.registrationStatus || s.status || 'Active').toUpperCase();
                    const isActive = statusStr === 'APPROVED' || statusStr === 'ACTIVE' || statusStr === '';
                    return (
                      <tr key={stId} style={{ borderBottom: '1px solid #f1f5f9' }}>
                        <td style={{ padding: '12px', fontWeight: '700', color: '#2563eb' }}>{stId}</td>
                        <td style={{ padding: '12px', fontWeight: '700', color: '#0f172a' }}>{s.name || s.studentName || 'Student'}</td>
                        <td style={{ padding: '12px', color: '#475569', fontWeight: '600' }}>Class {s.className || s.class || '—'}</td>
                        <td style={{ padding: '12px', color: '#475569' }}>{s.section || '—'}</td>
                        <td style={{ padding: '12px', color: '#475569' }}>{s.rollNo || '—'}</td>
                        <td style={{ padding: '12px', color: '#475569' }}>{s.fatherName || s.parentName || s.motherName || '—'}</td>
                        <td style={{ padding: '12px', color: '#2563eb' }}>{s.phone || s.parentPhone ? `📞 ${s.phone || s.parentPhone}` : '—'}</td>
                        <td style={{ padding: '12px' }}>
                          <span style={{ 
                            fontSize: '11px', fontWeight: '700', padding: '3px 8px', borderRadius: '12px',
                            background: isActive ? '#dcfce7' : '#fee2e2',
                            color: isActive ? '#166534' : '#991b1b'
                          }}>
                            {isActive ? 'ACTIVE' : 'INACTIVE'}
                          </span>
                        </td>
                        <td style={{ padding: '12px', textAlign: 'right' }}>
                          <button 
                            onClick={() => setSelectedStudentForView(s)}
                            style={{ background: '#eff6ff', border: '1px solid #bfdbfe', padding: '5px 10px', borderRadius: '5px', cursor: 'pointer', fontSize: '12px', fontWeight: '600', color: '#2563eb', display: 'inline-flex', alignItems: 'center', gap: '4px' }}
                          >
                            <Eye size={13} /> View Profile
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* Pagination Bar */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '16px', flexWrap: 'wrap', gap: '12px' }}>
              <span style={{ fontSize: '12.5px', color: '#64748b' }}>
                Showing {((currentPage - 1) * pageSize) + 1}–{Math.min(currentPage * pageSize, filteredStudents.length)} of {filteredStudents.length} Students
              </span>

              <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                <button 
                  disabled={currentPage === 1}
                  onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                  style={{ padding: '6px 12px', background: currentPage === 1 ? '#f1f5f9' : '#fff', border: '1px solid #cbd5e1', borderRadius: '6px', fontSize: '12px', cursor: currentPage === 1 ? 'not-allowed' : 'pointer', display: 'flex', alignItems: 'center', gap: '4px' }}
                >
                  <ChevronLeft size={14} /> Previous
                </button>

                <span style={{ padding: '0 8px', fontSize: '12.5px', fontWeight: '600', color: '#334155' }}>
                  Page {currentPage} of {totalPages}
                </span>

                <button 
                  disabled={currentPage >= totalPages}
                  onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                  style={{ padding: '6px 12px', background: currentPage >= totalPages ? '#f1f5f9' : '#fff', border: '1px solid #cbd5e1', borderRadius: '6px', fontSize: '12px', cursor: currentPage >= totalPages ? 'not-allowed' : 'pointer', display: 'flex', alignItems: 'center', gap: '4px' }}
                >
                  Next <ChevronRight size={14} />
                </button>
              </div>
            </div>
          </>
        )}
      </div>

      {/* ── View Student Profile Modal ── */}
      {selectedStudentForView && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
          <div style={{ background: '#ffffff', borderRadius: '12px', width: '90%', maxWidth: '600px', padding: '24px', boxShadow: '0 10px 25px rgba(0,0,0,0.2)', maxHeight: '90vh', overflowY: 'auto' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid #e2e8f0', paddingBottom: '12px', marginBottom: '16px' }}>
              <h3 style={{ margin: 0, fontSize: '18px', fontWeight: '700', color: '#0f172a' }}>
                👤 Student Master Profile
              </h3>
              <button onClick={() => setSelectedStudentForView(null)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#64748b' }}>
                <X size={20} />
              </button>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', fontSize: '13px', marginBottom: '20px' }}>
              <div>
                <span style={{ color: '#64748b', fontSize: '11px', fontWeight: '600', display: 'block' }}>STUDENT NAME</span>
                <strong style={{ color: '#0f172a', fontSize: '15px' }}>{selectedStudentForView.name || selectedStudentForView.studentName}</strong>
              </div>

              <div>
                <span style={{ color: '#64748b', fontSize: '11px', fontWeight: '600', display: 'block' }}>STUDENT / ROLL ID</span>
                <strong style={{ color: '#2563eb', fontSize: '15px' }}>{selectedStudentForView.studentId || selectedStudentForView.rollNo}</strong>
              </div>

              <div>
                <span style={{ color: '#64748b', fontSize: '11px', fontWeight: '600', display: 'block' }}>CLASS & SECTION</span>
                <strong style={{ color: '#0f172a' }}>Class {selectedStudentForView.className || selectedStudentForView.class || '—'} ({selectedStudentForView.section || 'Sec A'})</strong>
              </div>

              <div>
                <span style={{ color: '#64748b', fontSize: '11px', fontWeight: '600', display: 'block' }}>ACADEMIC YEAR</span>
                <strong style={{ color: '#0f172a' }}>{selectedStudentForView.admissionYear || academicYear}</strong>
              </div>

              <div>
                <span style={{ color: '#64748b', fontSize: '11px', fontWeight: '600', display: 'block' }}>GENDER / DOB</span>
                <strong>{selectedStudentForView.gender || '—'} • {selectedStudentForView.dob || '—'}</strong>
              </div>

              <div>
                <span style={{ color: '#64748b', fontSize: '11px', fontWeight: '600', display: 'block' }}>ADMISSION DATE</span>
                <strong>{formatDate(selectedStudentForView.admissionDate || selectedStudentForView.createdAt)}</strong>
              </div>

              <div>
                <span style={{ color: '#64748b', fontSize: '11px', fontWeight: '600', display: 'block' }}>FATHER NAME</span>
                <strong>{selectedStudentForView.fatherName || selectedStudentForView.parentName || '—'}</strong>
              </div>

              <div>
                <span style={{ color: '#64748b', fontSize: '11px', fontWeight: '600', display: 'block' }}>PARENT PHONE</span>
                <strong style={{ color: '#059669' }}>📞 {selectedStudentForView.phone || selectedStudentForView.parentPhone || '—'}</strong>
              </div>
            </div>

            <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
              <button onClick={() => setSelectedStudentForView(null)} style={{ padding: '8px 20px', background: '#2563eb', color: '#fff', border: 'none', borderRadius: '6px', fontSize: '13px', fontWeight: '600', cursor: 'pointer' }}>
                Close
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
};

export default StudentMasterList;
